import type { StageStatus, TodayVisit } from "@visits/visitsClient";

/** A household's patients booked into the same slot share one block. */
export interface CalendarVisitGroup {
  householdId: string;
  clientName: string | null;
  visits: TodayVisit[]; // always length >= 1
}

const slotKey = (visit: TodayVisit): string | null => {
  if (!visit.scheduledStart) return null;

  // End time too: appointments that merely start together keep their own geometry.
  return JSON.stringify([
    visit.householdId,
    visit.scheduledStart,
    visit.scheduledEnd,
  ]);
};

/** Groups by household id, never name (not identity-safe); input order is preserved. */
export const groupCalendarVisits = (
  visits: TodayVisit[],
): CalendarVisitGroup[] => {
  const visitsBySlot = new Map<string, TodayVisit[]>();
  for (const visit of visits) {
    const key = slotKey(visit);
    if (!key) continue;

    const bucket = visitsBySlot.get(key);
    if (bucket) bucket.push(visit);
    else visitsBySlot.set(key, [visit]);
  }

  const emittedSlots = new Set<string>();
  const groups: CalendarVisitGroup[] = [];
  for (const visit of visits) {
    const key = slotKey(visit);
    if (!key) {
      groups.push({
        householdId: visit.householdId,
        clientName: visit.clientName,
        visits: [visit],
      });
      continue;
    }

    if (emittedSlots.has(key)) continue;
    emittedSlots.add(key);
    groups.push({
      householdId: visit.householdId,
      clientName: visit.clientName,
      visits: visitsBySlot.get(key)!,
    });
  }

  return groups;
};

const MULTI_PATIENT_LABEL = "Multi-patient appointment";

interface CalendarGroupSummary {
  isMultiPatient: boolean;
  cancelled: boolean;
  accentStatus: StageStatus | undefined;
  /** Null for a single visit, where the caller renders the patient/client name. */
  title: string | null;
  /** Null for a single visit, or when it would repeat the title (unknown client). */
  subtitle: string | null;
}

export const summarizeCalendarGroup = (
  group: CalendarVisitGroup,
): CalendarGroupSummary => {
  const isMultiPatient = group.visits.length > 1;
  const accentStatus: StageStatus | undefined = group.visits.some(
    (visit) => visit.currentStage?.status === "active",
  )
    ? "active"
    : group.visits.some((visit) => visit.currentStage?.status === "done")
      ? "done"
      : undefined;

  return {
    isMultiPatient,
    cancelled: group.visits.every((visit) => visit.cancelledAt !== null),
    accentStatus,
    title: isMultiPatient
      ? `${group.clientName ?? MULTI_PATIENT_LABEL} (${group.visits.length})`
      : null,
    subtitle: isMultiPatient && group.clientName ? MULTI_PATIENT_LABEL : null,
  };
};
